/* eslint-disable no-underscore-dangle */
import React, { useEffect, useRef, useState } from 'react';
import ContractItem from 'components/molecules/contract_item';
import Loader from 'components/atoms/loader';
import EmptyResult from 'components/atoms/empty_result';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { ContractsState, ContractsTypes } from 'store/modules/contracts_store/types';
import ItemTools from 'components/molecules/item_tools';
import SelectButton from 'components/atoms/select_button';
import { getDownloadContracts } from 'services/actions/bidding_actions';
import ItemShare from 'components/atoms/item_share';
import Pagination from 'components/atoms/pagination';
import { toggleSelected } from 'services/toggleSelected';
import { useReactToPrint } from 'react-to-print';
import HelpVideoModal from 'components/molecules/help_video_modal';
import { hasCookie, setCookie } from 'services/actions/cookies_actions';
import OpenTutorial from 'components/atoms/open_tutorial';
import { Container } from './styles';

interface ContractListProps {
  setPage: React.Dispatch<React.SetStateAction<number>>;
  page: number;
}

const ContractList = (props: ContractListProps) => {
  const { setPage, page } = props;
  const dispatch = useDispatch();
  const [scrollToolsClass, setScrollToolsClass] = useState<string>('');
  const [wholePageSelected, setWholePageSelected] = useState<boolean>(false);
  const [selectedContracts, setSelectedContracts] = useState<Array<number>>([]);
  const [modalVideoOpened, setModalVideoOpened] = useState<boolean>(true);
  const [showShareWindow, setShowShareWindow] = useState<boolean>(false);
  const componentRef = useRef() as React.MutableRefObject<HTMLInputElement>;

  const { contracts, request } = useSelector<ApplicationState, ContractsState>(
    (state) => state.session.contracts,
  );

  useEffect(() => {
    setModalVideoOpened(!hasCookie('modalContratos'));
  }, []);

  useEffect(() => {
    const windowDiv = document.getElementById('windowDiv');
    windowDiv?.addEventListener('scroll', () => {
      setScrollToolsClass(windowDiv?.scrollTop > 100 ? 'border-bottom' : '');
    });
  }, []);

  useEffect(() => {
    if (modalVideoOpened) setCookie('modalContratos', true);
  }, [modalVideoOpened]);

  const toggleSelectedBiddings = (id: number, updateAll = false) => {
    toggleSelected({
      id,
      updateAll,
      selectedList: selectedContracts,
      setSelectedList: setSelectedContracts,
      sourceList: contracts?.contract,
      wholePageSelected,
    });
  };

  const downloadFile = (fileExtension: string) => {
    getDownloadContracts(
      fileExtension,
      'contratos',
      selectedContracts,
      selectedContracts.length,
      false,
    );
  };

  const handleShare = () => {
    setShowShareWindow(!showShareWindow);
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: 'conlicitacao',
  });

  const updateCurrentPage = (currentPage = 1) => {
    dispatch({ type: ContractsTypes.UPDATE_CURRENT_PAGE, currentPage });
    setPage(currentPage);
  };

  useEffect(() => {
    if (!contracts?.contract) return;

    setWholePageSelected(
      contracts?.contract
        ?.map((contract) => contract?._source?.id)
        .filter((contract) => selectedContracts.includes(contract))?.length ===
        contracts?.contract?.length,
    );
  }, [selectedContracts, contracts.currentPage, contracts.postsPerPage, contracts?.contract]);

  return (
    <Container className="px-md-5 py-4">
      <div className="d-md-flex d-block align-items-center">
        <h4 className="mb-md-0 mb-1 mr-3">Contratos</h4>
        <OpenTutorial type="modal" source="contracts" />
      </div>
      <div className={`row list-tools py-1 ${scrollToolsClass}`}>
        <div className="position-relative col-lg-6 d-block py-3 px-2">
          <SelectButton
            isChecked={wholePageSelected}
            id={0}
            toggleSelected={toggleSelectedBiddings}
            text="Selecionar página"
          />
          {selectedContracts.length > 0 && (
            <div className="itemToolsSelected ml-3">
              {selectedContracts.length}{' '}
              {selectedContracts.length === 1 ? 'registro selecionado.' : 'registros selecionados.'}
              <button
                type="button"
                className="btn btn-link m-0 py-0 px-1 border-0"
                onClick={() => setSelectedContracts([])}
              >
                Limpar seleção.
              </button>
            </div>
          )}
        </div>
        <ItemTools
          enabledButtons={selectedContracts?.length > 0}
          disabledPopoverLabel="o contrato"
          downloadFile={downloadFile}
          handleShare={handleShare}
          handlePrint={handlePrint}
        />
        {showShareWindow && (
          <ItemShare setShowShareWindow={setShowShareWindow} selectedItems={selectedContracts} />
        )}
      </div>
      <div className="col-md-2 p-0 bidding-info-title mb-1" />
      {request.loading && (
        <div className="d-flex align-items-center justify-content-center loading-container">
          <Loader />
        </div>
      )}
      <div ref={componentRef} className="row mx-1 mt-4">
        <div className="col-12">
          {request.loaded &&
            contracts?.contract?.map((contract, index) => (
              <ContractItem
                key={contract?._source?.id}
                contract={contract}
                contractIndex={index + (page - 1) * 20}
                toggleSelectContracts={toggleSelectedBiddings}
                isChecked={selectedContracts.includes(contract?._source?.id)}
              />
            ))}
        </div>
      </div>
      <HelpVideoModal
        modalVideoOpened={modalVideoOpened}
        setModalVideoOpened={setModalVideoOpened}
        urlVideo="https://player.vimeo.com/video/1142396672"
      />
      {request.loaded && contracts?.contract?.length === 0 && (
        <EmptyResult
          title="Desculpe!"
          description="Não temos contratos que correspondam a esses filtros."
        />
      )}
      {request.loaded && contracts.total_entries !== 0 && (
        <div className="row d-print-none">
          <div className="col-md-12 px-4 mb-5 pb-5">
            <Pagination
              postsPerPage={contracts?.postsPerPage}
              totalPosts={contracts?.total_entries}
              paginate={updateCurrentPage}
              currentPage={page}
              firstAndLastPage
            />
          </div>
        </div>
      )}
    </Container>
  );
};
export default ContractList;
